import { broadcastRates } from "@/partner/entities/marketing";
import { formatNumber } from "@/partner/shared/utils/formatLocale";

/**
 * Опрос открытой запущенной автоматизации: статистика в шапке, воронка и
 * участники на открытой вкладке обновляются с одним шагом.
 */
export const AUTOMATION_POLL_INTERVAL_MS = 30_000;

export interface NodeStatsInput {
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  skipped: { reason: string; count: number }[];
  /** Сколько прохождений цепочки дошли до шага (фаза 4). */
  entered?: number | null;
}

export interface NodeBadgeValues {
  sent: number;
  /** Доля открытий от доставленных; null, пока доставок нет. */
  openRate: number | null;
  skipped: number;
  /** Дошли до шага цепочки. */
  entered: number;
}

// Счётчик с бэкенда не должен уйти в минус на кабинете из-за дрейфа
// агрегата: показываем 0, а не отрицательное число.
const nonNegative = (value: number): number => Math.max(0, value);

/**
 * Бейджи шага: письма, открытия, пропуски и (у цепочки) сколько дошли до
 * шага. Шаг без писем, пропусков и дошедших без бейджей; шаг цепочки без
 * писем (ожидание, условие) получает значения, если до него кто-то дошёл.
 */
export function nodeBadgeValues(
  stats: NodeStatsInput | undefined
): NodeBadgeValues | null {
  if (!stats) return null;
  const sent = nonNegative(stats.sent);
  const skipped = stats.skipped.reduce(
    (sum, item) => sum + nonNegative(item.count),
    0
  );
  const entered = nonNegative(stats.entered ?? 0);
  if (sent === 0 && skipped === 0 && entered === 0) return null;
  const rates = broadcastRates({
    sent,
    delivered: nonNegative(stats.delivered),
    opened: nonNegative(stats.opened),
    clicked: nonNegative(stats.clicked),
    purchases: 0,
    revenueUSD: 0,
  });
  return { sent, openRate: rates.open, skipped, entered };
}

export interface AutomationStatsInput {
  enrolled: number;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  purchases: number;
  revenueUSD: number;
  /**
   * Продления подписок, пришедших по письму (вместе со счетами Stripe
   * Billing), и сумма первых покупок с продлениями. Считает бэкенд.
   */
  renewals?: number | null;
  renewalRevenueUSD?: number | null;
  revenueWithRenewalsUSD?: number | null;
  /** Цепочка (фаза 4): в пути, дошли до цели, остановлены правилом выхода. */
  active?: number | null;
  goalReached?: number | null;
  stopped?: number | null;
}

export interface StripMetrics {
  enrolled: number;
  sent: number;
  deliveryRate: number | null;
  openRate: number | null;
  clickRate: number | null;
  revenueUSD: number;
  renewals: number;
  revenueWithRenewalsUSD: number;
  active: number;
  goalReached: number;
  stopped: number;
  /** Доля дошедших до цели от вошедших в цепочку (0..1); null, пока никто не вошёл. */
  conversion: number | null;
}

/** Шапка статистики автоматизации: те же доли, что у рассылок. */
export function stripMetrics(stats: AutomationStatsInput): StripMetrics {
  // Фикс-раунд 1, M1: доли считаем от уже неотрицательных счётчиков, а не
  // от сырых значений с бэкенда: иначе отрицательный delivered при
  // положительном sent давал отрицательный процент доставки.
  const rates = broadcastRates({
    sent: nonNegative(stats.sent),
    delivered: nonNegative(stats.delivered),
    opened: nonNegative(stats.opened),
    clicked: nonNegative(stats.clicked),
    purchases: nonNegative(stats.purchases),
    revenueUSD: nonNegative(stats.revenueUSD),
  });
  const enrolled = nonNegative(stats.enrolled);
  const goalReached = nonNegative(stats.goalReached ?? 0);
  return {
    enrolled,
    sent: nonNegative(stats.sent),
    deliveryRate: rates.delivery,
    openRate: rates.open,
    clickRate: rates.click,
    revenueUSD: nonNegative(stats.revenueUSD),
    renewals: nonNegative(stats.renewals ?? 0),
    revenueWithRenewalsUSD: nonNegative(stats.revenueWithRenewalsUSD ?? 0),
    active: nonNegative(stats.active ?? 0),
    goalReached,
    stopped: nonNegative(stats.stopped ?? 0),
    conversion: enrolled > 0 ? goalReached / enrolled : null,
  };
}

/**
 * Число с разделителем тысяч по языку интерфейса (I6): без него «12345»
 * вместо «12 345» / «12,345» в шапке статистики и бейджах шагов. Общий
 * форматтер кабинета с кешем по языку (супер-ревью 3 и 7).
 */
export const formatCount = (value: number, locale: string): string =>
  formatNumber(value, locale);
